/** ADGS runs for one local calendar day, from GitHub Actions. */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { getAdgsRefetchInterval } from './adgs-sweeps-polling';
import { adgsSweepOutcome, isUnsuccessfulSweep } from './adgs-sweep-outcome';
import { OutcomeBadge } from './OutcomeBadge';
import { RunnerBadge } from './RunnerBadge';
import {
  fmtDuration,
  fmtLocal,
  fmtLocalEnd,
  fmtUtc,
  localDayValue,
  localDayWindow,
  localZoneAbbrev,
} from './shared';

function TriggerChip({ trigger }: { trigger: string }) {
  const style =
    trigger === 'manual'
      ? 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300'
      : trigger === 'schedule'
        ? 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
  return (
    <span
      className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-medium ${style}`}
    >
      {trigger}
    </span>
  );
}

export function AdgsSweepsTab() {
  // Today in the VIEWER's zone, and the instants that day spans. The server is
  // never asked to decide what "today" is — it cannot know the zone.
  const [day, setDay] = useState(() => localDayValue());
  const [unsuccessfulOnly, setUnsuccessfulOnly] = useState(false);
  // Not named `window` — shadowing the global in a component is how a later
  // edit reaching for `window.setTimeout` silently breaks.
  const dayWindow = useMemo(() => localDayWindow(day), [day]);

  const sweepsQuery = useQuery({
    // The day is part of the key, so switching days is a cache lookup rather
    // than a refetch of the same bucket under a different meaning.
    queryKey: ['adgs-sweeps', day],
    queryFn: () => api.getAdgsSweeps(dayWindow.from, dayWindow.to),
    refetchInterval: (query) => getAdgsRefetchInterval(query.state.data?.sweeps),
  });

  const allSweeps = sweepsQuery.data?.sweeps ?? [];
  const sweeps = unsuccessfulOnly ? allSweeps.filter(isUnsuccessfulSweep) : allSweeps;
  const zone = localZoneAbbrev();

  const controls = (
    <div className="flex flex-wrap items-center gap-4">
      <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
        Day
        <input
          type="date"
          value={day}
          max={localDayValue()}
          onChange={(e) => e.target.value && setDay(e.target.value)}
          className="rounded border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
        <input
          type="checkbox"
          checked={unsuccessfulOnly}
          onChange={(e) => setUnsuccessfulOnly(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        Only unsuccessful runs
      </label>
      <span className="text-xs text-zinc-500">
        Runs started on {day}
        {zone ? ` (${zone})` : ''}
      </span>
    </div>
  );

  let body;
  if (sweepsQuery.isLoading) {
    body = <div className="p-6 text-sm text-zinc-500">Loading sweeps from GitHub…</div>;
  } else if (sweepsQuery.isError) {
    body = (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
        Sweep history failed to load. Check Worker logs for /admin/autobuild/adgs/sweeps; a 503
        names the missing GitHub credential.
      </div>
    );
  } else if (allSweeps.length === 0) {
    body = (
      <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
        No ADGS runs started on {day}.
      </div>
    );
  } else if (sweeps.length === 0) {
    body = (
      <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
        All {allSweeps.length} runs on {day} succeeded.
      </div>
    );
  } else {
    body = (
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs text-zinc-500 dark:bg-zinc-900">
            <tr>
              <th className="px-3 py-2 font-medium">Run</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Runner</th>
              <th className="px-3 py-2 font-medium">Trigger</th>
              <th className="px-3 py-2 font-medium">Started</th>
              <th className="px-3 py-2 font-medium">Finished</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              <th className="px-3 py-2 text-right font-medium">Evaluated</th>
              <th className="px-3 py-2 font-medium">Dispatched</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white dark:divide-zinc-800 dark:bg-zinc-950">
            {sweeps.map((sweep) => (
              <tr key={sweep.runId} className="align-top hover:bg-zinc-50 dark:hover:bg-zinc-900">
                <td className="px-3 py-2">
                  <a
                    href={sweep.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-xs text-zinc-800 hover:underline dark:text-zinc-200"
                  >
                    {sweep.runId} <ExternalLink className="h-3 w-3 text-zinc-400" />
                  </a>
                </td>
                <td className="px-3 py-2">
                  <OutcomeBadge outcome={adgsSweepOutcome(sweep)} />
                </td>
                <td className="px-3 py-2">
                  {sweep.runnerHost == null ? (
                    <span className="text-xs text-zinc-400" title="GitHub recorded no runner">
                      —
                    </span>
                  ) : (
                    <RunnerBadge runnerHost={sweep.runnerHost} runnerName={sweep.runnerName} />
                  )}
                </td>
                <td className="px-3 py-2">
                  <TriggerChip trigger={sweep.trigger} />
                </td>
                <td
                  className="whitespace-nowrap px-3 py-2 text-xs"
                  title={`${fmtUtc(sweep.startedAt)} — displayed in your timezone`}
                >
                  {fmtLocal(sweep.startedAt)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs">
                  {sweep.completedAt ? (
                    <span title={fmtUtc(sweep.completedAt)}>
                      {fmtLocalEnd(sweep.startedAt, sweep.completedAt)}
                    </span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">
                  {sweep.durationSeconds == null ? '—' : fmtDuration(sweep.durationSeconds)}
                </td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">
                  {sweep.evaluatedCount == null ? (
                    <span
                      className="text-amber-600 dark:text-amber-400"
                      title={sweep.detailsError ?? 'Run details are not readable yet'}
                    >
                      Unknown
                    </span>
                  ) : (
                    sweep.evaluatedCount
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  {sweep.dispatchedTicketIds == null ? (
                    <span
                      className="text-amber-600 dark:text-amber-400"
                      title={sweep.detailsError ?? 'Dispatch details are not readable yet'}
                    >
                      Unknown
                    </span>
                  ) : sweep.dispatchedTicketIds.length === 0 ? (
                    <span className="text-zinc-400">None</span>
                  ) : (
                    <div className="flex max-w-[20rem] flex-wrap gap-1">
                      {sweep.dispatchedTicketIds.map((id) => (
                        <a
                          key={id}
                          href={`https://linear.app/apptonomy/issue/${id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] text-blue-700 hover:underline dark:bg-blue-950/40 dark:text-blue-300"
                        >
                          {id}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {controls}
      {sweepsQuery.data?.truncated && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          This day hit the page cap, so the oldest runs of {day} are missing from the table.
        </div>
      )}
      {body}

      <div className="text-[11px] text-zinc-400">
        Run timing, trigger, conclusion, and runner come from GitHub Actions. Evaluated counts come
        from the ADGS job log; dispatched ticket ids come from the matching CDID workflow-dispatch
        title. An unknown value is shown explicitly when GitHub has not produced readable detail
        yet. These are model-free control-plane sweeps, so a healthy run normally finishes in
        seconds.
      </div>
    </div>
  );
}
